import { computed, createModel, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { QueryObserver } from '@tanstack/react-query';
import type { QueryClient, QueryObserverResult } from '@tanstack/react-query';
import type {
    BacktestComplexityEstimate,
    BacktestComplexityEstimateRequest,
    BacktestComplexityTier,
} from '@thinkalpha/platform-ws-client/contracts/backtest.js';
import debounce from 'lodash.debounce';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import { getBacktestComplexityEstimateQuery } from 'src/queries/backtestSpecs';

export type ComplexityStatus = 'idle' | 'pending' | 'error';

interface ComplexityReducerState {
    readonly estimate: BacktestComplexityEstimate | null;
    readonly status: ComplexityStatus;
}

type ComplexityAction =
    | {
          type: 'SET_ESTIMATE';
          result: QueryObserverResult<BacktestComplexityEstimate | null, Error>;
      }
    | { type: 'CLEAR_ESTIMATE'; nextStatus: ComplexityStatus };

function statusFromResult(result: QueryObserverResult<BacktestComplexityEstimate | null, Error>): ComplexityStatus {
    if (result.isFetching) return 'pending';
    if (result.isError) return 'error';
    return 'idle';
}

function reducer(action: ComplexityAction): ComplexityReducerState {
    switch (action.type) {
        case 'SET_ESTIMATE':
            return {
                estimate: action.result.data ?? null,
                status: statusFromResult(action.result),
            };
        case 'CLEAR_ESTIMATE':
            return { estimate: null, status: action.nextStatus };
        default:
            throw new UnreachableCaseError(action);
    }
}

export interface ComplexityModelInit {
    queryClient: QueryClient;
    request: ReadonlySignal<BacktestComplexityEstimateRequest | null>;
}

export const ComplexityModel = createModel((init: ComplexityModelInit) => {
    const state = signal<ComplexityReducerState>({
        estimate: null,
        status: 'idle',
    });

    const dispatch = (action: ComplexityAction): void => {
        state.value = reducer(action);
    };

    const estimate: ReadonlySignal<BacktestComplexityEstimate | null> = computed(() => state.value.estimate);
    const status: ReadonlySignal<ComplexityStatus> = computed(() => state.value.status);
    const tier: ReadonlySignal<BacktestComplexityTier | null> = computed(() => state.value.estimate?.tier ?? null);
    const isBlocking: ReadonlySignal<boolean> = computed(() => {
        if (init.request.value == null) return false;
        if (state.value.status !== 'idle') return true;
        return state.value.estimate?.tier === 'very-high';
    });

    const observer = new QueryObserver<BacktestComplexityEstimate | null, Error>(
        init.queryClient,
        getBacktestComplexityEstimateQuery(init.request.peek()),
    );

    const applyOptions = debounce((req: BacktestComplexityEstimateRequest | null) => {
        observer.setOptions(getBacktestComplexityEstimateQuery(req));
        dispatch({ type: 'SET_ESTIMATE', result: observer.getCurrentResult() });
    }, 500);

    effect(() => {
        const req = init.request.value;
        // Drop the prior estimate synchronously so the 500ms debounce window
        // don't expose a value that doesn't match the current options.
        dispatch({ type: 'CLEAR_ESTIMATE', nextStatus: req == null ? 'idle' : 'pending' });
        applyOptions(req);
        return () => applyOptions.cancel();
    });

    effect(() => {
        const unsubscribe = observer.subscribe((result) => {
            dispatch({ type: 'SET_ESTIMATE', result });
        });
        // Seed reducer state with the current observer snapshot.
        dispatch({ type: 'SET_ESTIMATE', result: observer.getCurrentResult() });
        return () => {
            unsubscribe();
            observer.destroy();
        };
    });

    return {
        estimate,
        status,
        tier,
        isBlocking,
    };
});

export type ComplexityModel = InstanceType<typeof ComplexityModel>;
